"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  Car,
  TrainFront,
  Bus,
  KeyRound,
  ChevronRight,
  Clock,
  Phone,
  MapPin,
  CreditCard,
  ShieldCheck,
  Route,
  CalendarClock,
  Bus as BusIcon,
  SquareParking,
  BusFront,
} from "lucide-react";

const hubCards = [
  {
    key: "taxi",
    href: "/transporte/taxi",
    icon: Car,
    accent: "from-[#ffb800] to-[#e6a700]",
    iconBg: "bg-[#102542]/15 text-[#102542]",
    darkText: true,
  },
  {
    key: "metro",
    href: "/transporte/metro",
    icon: TrainFront,
    accent: "from-[#102542] to-[#1B3B5F]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
  {
    key: "metrobus",
    href: "/transporte/metrobus",
    icon: Bus,
    accent: "from-[#1B3B5F] to-[#2C5282]",
    iconBg: "bg-white/15 text-white",
    darkText: false,
  },
  {
    key: "rental",
    href: "/transporte/alquiler-autos",
    icon: KeyRound,
    accent: "from-[#4F6D94] to-[#102542]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
  {
    key: "parking",
    href: "/transporte/estacionamientos",
    icon: SquareParking,
    accent: "from-[#2C5282] to-[#102542]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
  {
    key: "shuttle",
    href: "/transporte/shuttlebus",
    icon: BusFront,
    accent: "from-[#1B3B5F] to-[#4F6D94]",
    iconBg: "bg-[#ffb800]/15 text-[#ffb800]",
    darkText: false,
  },
] as const;

const quickInfo = [
  { key: "availability", icon: Clock },
  { key: "reserve", icon: Phone },
  { key: "stations", icon: MapPin },
  { key: "payments", icon: CreditCard },
] as const;

const transportFeatures = [
  { key: "authorized", icon: ShieldCheck },
  { key: "routes", icon: Route },
  { key: "noWaits", icon: CalendarClock },
  { key: "forEveryone", icon: BusIcon },
] as const;

export default function Transporte() {
  const t = useTranslations("transportePage");

  return (
    <main className="bg-white mx-auto">
      {/* Banner con imagen */}
      <section className="relative w-full overflow-hidden h-[55vh] min-h-[380px] max-h-[520px]">
        <Image
          src="https://galeria.tocumenaero.com/gallery/api/Terminal%201%20y%202%20fachada%20d%C3%ADa%20y%20noche/321A5606.jpg"
          alt={t("hero.imageAlt")}
          fill
          priority
          sizes="100vw"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#102542]/80 via-[#102542]/60 to-[#102542]/85" />

        <div className="relative z-10 h-full max-w-6xl mx-auto px-6 flex flex-col items-center justify-center text-center text-white">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs md:text-sm uppercase tracking-widest text-white/90 mb-5">
            <Car className="w-4 h-4 text-[#ffb800]" />
            {t("hero.badge")}
          </span>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight drop-shadow-lg">
            {t("hero.titlePart1")}{" "}
            <span className="text-[#ffb800]">{t("hero.titleHighlight")}</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80 text-base md:text-lg">
            {t("hero.subtitle")}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#ffb800] to-transparent mx-auto rounded-full mt-5" />
        </div>
      </section>

      {/* 4 Cards Principales */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mb-3">
              {t("hubSection.title")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("hubSection.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hubCards.map(
              ({ key, href, icon: Icon, accent, iconBg, darkText }) => (
                <Link
                  key={key}
                  href={href}
                  className={`group relative rounded-2xl p-6 bg-gradient-to-br ${accent} shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden`}
                >
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/0 to-white/0 group-hover:to-white/10 transition-all duration-700 pointer-events-none" />

                  <div className="relative z-10 flex flex-col h-full min-h-[260px]">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${iconBg} transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-6`}
                    >
                      <Icon className="w-7 h-7" />
                    </div>

                    <h3
                      className={`text-xl font-bold mb-3 ${
                        darkText ? "text-[#102542]" : "text-white"
                      }`}
                    >
                      {t(`hubCards.${key}.title`)}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed mb-6 flex-1 ${
                        darkText ? "text-[#102542]/80" : "text-white/85"
                      }`}
                    >
                      {t(`hubCards.${key}.description`)}
                    </p>

                    <div
                      className={`inline-flex items-center gap-1 text-sm font-semibold transition-all duration-300 group-hover:gap-2 ${
                        darkText ? "text-[#102542]" : "text-white"
                      }`}
                    >
                      {t("hubSection.seeMore")}
                      <ChevronRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              ),
            )}
          </div>
        </div>
      </section>

      {/* Información rápida */}
      <section className="w-full py-16 md:py-20 bg-[#102542] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute top-20 right-20 w-80 h-80 bg-[#ffb800] rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
                {t("quickInfoSection.tag")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-3 mb-4">
                {t("quickInfoSection.title")}
              </h2>
              <p className="text-white/70 leading-relaxed">
                {t("quickInfoSection.description")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickInfo.map(({ key, icon: Icon }) => (
                <div
                  key={key}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-[#ffb800]/40 transition-all duration-300 flex flex-col"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#ffb800]/15 text-[#ffb800] flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">
                    {t(`quickInfo.${key}.title`)}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {t(`quickInfo.${key}.description`)}
                  </p>
                  {key === "reserve" && (
                    <Link
                      href="/transporte/taxi"
                      className="mt-4 inline-flex items-center justify-center gap-1.5 self-start bg-[#ffb800] text-[#102542] px-4 py-2 rounded-full text-sm font-semibold hover:bg-white transition-all duration-300 hover:shadow-lg"
                    >
                      {t("quickInfo.reserve.cta")}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Características del transporte */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#ffb800] text-sm uppercase tracking-widest font-semibold">
              {t("featuresSection.tag")}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mt-3 mb-3">
              {t("featuresSection.title")}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t("featuresSection.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {transportFeatures.map(({ key, icon: Icon }) => (
              <div
                key={key}
                className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#ffb800]/40 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-[#102542] text-[#ffb800] flex items-center justify-center mb-4 group-hover:bg-[#ffb800] group-hover:text-[#102542] transition-all duration-500">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-[#102542] mb-2">
                  {t(`transportFeatures.${key}.title`)}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t(`transportFeatures.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-br from-[#ffb800] to-[#e6a700]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#102542] mb-4">
            {t("cta.title")}
          </h2>
          <p className="text-[#102542]/80 text-lg mb-8 max-w-2xl mx-auto">
            {t("cta.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/transporte/taxi"
              className="inline-flex items-center justify-center gap-2 bg-[#102542] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#1B3B5F] transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
            >
              <Car className="w-5 h-5" />
              {t("cta.bookTaxi")}
            </Link>
            <Link
              href="/informacion/centros-informacion"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#102542] px-8 py-4 rounded-full font-semibold hover:bg-[#102542] hover:text-white transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 border-2 border-[#102542]"
            >
              {t("cta.infoCenter")}
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
